'use client';

import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import styles from './Button.module.css';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'solid' | 'outline' | 'ghost' | 'link';
  colorScheme?: 'blue' | 'red' | 'green' | 'purple' | 'teal' | 'gray';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isFullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'solid',
      colorScheme = 'purple',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      isFullWidth = false,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const baseClass = styles.button;
    const variantClass = styles[`button-${variant}`];
    const colorClass = styles[`button-${colorScheme}`];
    const sizeClass = styles[`button-${size}`];
    const fullWidthClass = isFullWidth ? styles['button-full-width'] : '';
    
    const classes = [
      baseClass,
      variantClass,
      colorClass,
      sizeClass,
      fullWidthClass,
      className
    ].filter(Boolean).join(' ');
    
    return (
      <button
        ref={ref}
        className={classes}
        disabled={isLoading || disabled}
        {...props}
      >
        {isLoading && (
          <span className={styles['button-spinner']}>
            <svg className={styles['spinner-icon']} viewBox="0 0 24 24">
              <circle
                className={styles['spinner-circle']}
                cx="12"
                cy="12"
                r="10"
                fill="none"
                strokeWidth="4"
              />
            </svg>
          </span>
        )}
        {leftIcon && !isLoading && (
          <span className={styles['button-icon-left']}>{leftIcon}</span>
        )}
        {children}
        {rightIcon && !isLoading && (
          <span className={styles['button-icon-right']}>{rightIcon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
